import { httpClient } from "@bw/shared/api";
import type { UUID } from "@bw/shared/types";
import type {
  FacilityResponse,
  OrgResponse,
  PlatformOrgListResponse,
  ReviewNoteResponse,
  Schema,
} from "@bw/shared/types/generated";

export type ReconciliationResponse = Schema["ReconciliationResponse"];
export type PeriodReconciliationResponse =
  Schema["PeriodReconciliationResponse"];

export interface ListOrgsParams {
  status?: string;
  search?: string;
  limit?: number;
  offset?: number;
}

export async function listOrgs(
  params?: ListOrgsParams,
): Promise<PlatformOrgListResponse> {
  const sp = new URLSearchParams();
  if (params?.status) sp.set("status", params.status);
  if (params?.search) sp.set("search", params.search);
  if (params?.limit !== undefined) sp.set("limit", String(params.limit));
  if (params?.offset !== undefined) sp.set("offset", String(params.offset));
  const qs = sp.toString();
  return httpClient.get<PlatformOrgListResponse>(
    `/platform/orgs${qs ? `?${qs}` : ""}`,
  );
}

export async function getOrg(orgId: UUID): Promise<OrgResponse> {
  return httpClient.get<OrgResponse>(`/platform/orgs/${orgId}`);
}

export async function approveOrg(orgId: UUID): Promise<void> {
  return httpClient.post<void>(`/platform/orgs/${orgId}/approve`);
}

export async function suspendOrg(orgId: UUID, reason: string): Promise<void> {
  const payload: Schema["SuspendRequest"] = { reason };
  return httpClient.post<void>(`/platform/orgs/${orgId}/suspend`, payload);
}

export async function startReview(orgId: UUID): Promise<void> {
  return httpClient.post<void>(`/platform/orgs/${orgId}/start-review`);
}

export async function rejectOrg(orgId: UUID, reason: string): Promise<void> {
  const payload: Schema["OrgRejectRequest"] = { reason };
  return httpClient.post<void>(`/platform/orgs/${orgId}/reject`, payload);
}

export async function reactivateOrg(orgId: UUID): Promise<void> {
  return httpClient.post<void>(`/platform/orgs/${orgId}/reactivate`);
}

export async function listOrgFacilities(
  orgId: UUID,
): Promise<FacilityResponse[]> {
  return httpClient.get<FacilityResponse[]>(
    `/platform/orgs/${orgId}/facilities`,
  );
}

export async function addNote(
  orgId: UUID,
  note: string,
  noteType = "internal",
): Promise<ReviewNoteResponse> {
  const payload: Schema["OrgReviewNoteCreate"] = {
    note,
    note_type: noteType,
  };
  return httpClient.post<ReviewNoteResponse>(
    `/platform/orgs/${orgId}/notes`,
    payload,
  );
}

export async function getNotes(orgId: UUID): Promise<ReviewNoteResponse[]> {
  return httpClient.get<ReviewNoteResponse[]>(`/platform/orgs/${orgId}/notes`);
}

/**
 * Per-period reconciliation for one org (platform_admin): computed net owed
 * (accrual) vs what was actually paid out, with the SIGNED delta and a
 * reconciliation_status per payout period.
 *
 * NOTE the endpoint shape — reconciliation lives under the platform FINANCE
 * namespace with `org_id` as a REQUIRED query param, NOT
 * `/platform/orgs/{id}/reconciliation`. httpClient prefixes /bff/api, so we pass
 * a backend-space path. Every figure is rendered VERBATIM by the component (no
 * client-side money math).
 */
export async function getOrgReconciliation(
  orgId: string,
  periods = 6,
): Promise<ReconciliationResponse> {
  return httpClient.get<ReconciliationResponse>(
    `/platform/finance/reconciliation?org_id=${encodeURIComponent(orgId)}&periods=${periods}`,
  );
}
